import {FieldValues} from "react-hook-form/dist/types/fields";
import {FieldPath, useFormContext} from "react-hook-form";
import {useCallback} from "react";
import {PathValue} from "react-hook-form/dist/types";
import {Conversione} from "@commons/utils/Conversione";

export function useRangeValidator<T extends FieldValues>(nameInizio: FieldPath<T>,
                                                         nameFine: FieldPath<T>,
                                                         nameQuantita: FieldPath<T>,
                                                         optionalFields?: boolean,
                                                         onChangeRange?: (inizio: number, fine: number, durata: number) => void,
                                                         onNotANumber?: (inizio: number, fine: number, durata: number) => void,
) {
    const {getValues, setValue, trigger} = useFormContext<T>();

    const orarioRequired = useCallback((v: any) => {
        return typeof v === 'string' && (v !== null && v !== '');
    }, []);

    const formatOrario = useCallback((v: any) => {
        return orarioRequired(v) && (v.length === 4);// && (v !== '0000'); rimosso il 21/02/2024
    }, [orarioRequired]);

    const checkIntervalloFacoltativo = useCallback((v: any) => {
        if (optionalFields) {
            return (v === '' || v === '0000' || formatOrario(v));
        } else return false;
    }, [formatOrario, optionalFields]);

    function orarioLessThan(v: any, onInvalidValue?: () => void) {
        const currentValueFine = getValues(nameFine);
        if (formatOrario(currentValueFine)) {
            const start = Number.parseInt(v);
            const stop = Number.parseInt(currentValueFine!);
            let valid;
            if (stop === 0 && start > 0) {
                valid = true; // zero sull'estremo finale rappresenta la mezzanotte
            } else {
                valid = start < stop;
            }
            if (!valid && onInvalidValue) onInvalidValue();
            return valid;
        } else return true;
    }

    function orarioMoreThan(v: any, onInvalidValue?: () => void) {
        const currentValueInizio = getValues(nameInizio);
        if (formatOrario(currentValueInizio)) {
            const start = Number.parseInt(currentValueInizio!);
            const stop = Number.parseInt(v);
            let valid;
            if (stop === 0 && start > 0) {
                valid = true; // zero sull'estremo finale rappresenta la mezzanotte
            } else {
                valid = stop > start;
            }
            if (!valid && onInvalidValue) onInvalidValue();
            return valid;
        } else return true;
    }

    // Rivalido ora fine al variare di ora inizio
    const onChangeOraInizio = (v: string) => {
        if (formatOrario(v)) {
            const currentValueFine = getValues(nameFine);
            if (formatOrario(currentValueFine)) {
                const {entrata, uscita, durata} = getDurataIntervallo(v, currentValueFine);
                setValue(nameQuantita, durata as PathValue<any, any>);
                trigger([nameFine, nameQuantita]).then(() => {
                });
                if (onChangeRange)
                    onChangeRange(entrata, uscita, durata);
            } else {
                setValue(nameQuantita, 0 as PathValue<any, any>);
            }
        } else {
            setValue(nameQuantita, 0 as PathValue<any, any>);
        }
    };
    // Rivalido ora inizio al variare di ora fine
    const onChangeOraFine = (v: string) => {
        if (formatOrario(v)) {
            const currentValueInizio = getValues(nameInizio);
            if (formatOrario(currentValueInizio)) {
                const {entrata, uscita, durata} = getDurataIntervallo(currentValueInizio, v);
                setValue(nameQuantita, durata as PathValue<any, any>);
                trigger([nameInizio, nameQuantita]).then(() => {
                });
                if (onChangeRange)
                    onChangeRange(entrata, uscita, durata);
            } else {
                setValue(nameQuantita, 0 as PathValue<any, any>);
            }
        } else {
            setValue(nameQuantita, 0 as PathValue<any, any>);
        }
    };

    const getDurataIntervallo = (inizio: string, fine: string) => {
        let durata = 0;
        let entrata = Conversione.hhmm2hhcc(inizio);
        let uscita = Conversione.hhmm2hhcc(fine);
        if (!(entrata === 0 && uscita === 0)) {
            durata = uscita > entrata ? uscita - entrata : 24.0 - entrata + uscita;
        }
        return {entrata, uscita, durata};
    };

    // Verifico se quantità congrua a inizio e fine
    const onChangeQuantita = (v: string) => {
        //console.log('quantità da parametro: ', v);
        const quantitaInput = Number.parseFloat(v);
        if (!isNaN(quantitaInput)) {
            if (optionalFields) {
                const currentValueInizio = getValues(nameInizio);
                const currentValueFine = getValues(nameFine);
                const {entrata, uscita, durata: quantitaCalcolata} = getDurataIntervallo(currentValueInizio, currentValueFine);
                if (quantitaCalcolata !== quantitaInput) {
                    setValue(nameInizio, '0000' as PathValue<any, any>);
                    setValue(nameFine, '0000' as PathValue<any, any>);
                }
                if (onChangeRange) {
                    onChangeRange(entrata, uscita, quantitaInput);
                }
            } else {
                if (onChangeRange) {
                    const currentValueInizio = getValues(nameInizio);
                    const currentValueFine = getValues(nameFine);
                    const {entrata, uscita, durata} = getDurataIntervallo(currentValueInizio, currentValueFine);
                    onChangeRange(entrata, uscita, durata);
                }
            }
        } else {
            if (onNotANumber) {
                const currentValueInizio = getValues(nameInizio);
                const currentValueFine = getValues(nameFine);
                const {entrata, uscita, durata} = getDurataIntervallo(currentValueInizio, currentValueFine);
                onNotANumber(entrata, uscita, durata);
            }
        }
    }
    return {orarioRequired, formatOrario, checkIntervalloFacoltativo, orarioLessThan, orarioMoreThan, onChangeOraInizio, onChangeOraFine, onChangeQuantita};
}